import React, { createContext, useContext, useEffect, useState } from 'react';
import { useConcatenateFilterSelection } from '../../../../components/filters/hooks/common.hook';
import { FILTER_NAMES } from '../../../../components/filters/hooks/filter-names.constants';
import {
  useFilterCompactViewSelection,
  useFilterMemoSelection,
} from '../../../../components/filters/hooks/report-filter-selection.hook';
import { useEncodeFilters } from '../../../../components/filters/hooks/use-encode-filters.hook';
import { useFilterComparisonPeriodsSelection } from '../../../../components/filters/hooks/use-filter-comparison-periods-selection.hook';
import { useFilterDrilltroughSelection } from '../../../../components/filters/hooks/use-filter-drillthrough-selection.hook';
import { useFilterPanelSelection } from '../../../../components/filters/hooks/use-filter-panel-selection.hook';
import { FilterEncoderComponentProps } from '../../../../components/filters/interfaces/filterable-component-props.interface';
import { FilterSelectedValue } from '../../../../hooks/filters/interfaces/filter-selected-value.interface';
import { useAppFilterSelectedValue } from '../../../../hooks/filters/use-app-filter-selected-value.hook';
import { useResetFiltersOnMetaCategoryChange } from '../../hooks/use-reset-filter-on-meta-change';

interface NetflowFilterContextType {
  applyFilters: () => void;
  filterSelection: FilterSelectedValue[] | null;
  differenceCount: number;
  isApplyDisabled: boolean;
}

const REQUIRED_FILTER_IDS = [
  FILTER_NAMES.METACATEGORY,
  FILTER_NAMES.CATEGORYSINGLE,
];

const NetflowFilterContext = createContext<
  NetflowFilterContextType | undefined
>(undefined);

const serializeValues = (f: FilterSelectedValue) =>
  f.values
    .map((v) => JSON.stringify(v.value))
    .sort()
    .join('|');

export const NetflowFilterProvider = ({
  children,
}: FilterEncoderComponentProps) => {
  const [appliedFilterSelection, setAppliedFilterSelection] = useState<
    FilterSelectedValue[] | null
  >(null);

  const [differenceCount, setDifferenceCount] = useState(0);

  const filterSelection = useConcatenateFilterSelection(
    useFilterCompactViewSelection(),
    useFilterPanelSelection(),
    useFilterDrilltroughSelection(),
    useFilterComparisonPeriodsSelection(),
    useFilterMemoSelection(FILTER_NAMES.PERIODLENGTH),
    useFilterMemoSelection(FILTER_NAMES.PRODUCTHIER1),
    useFilterMemoSelection(FILTER_NAMES.DATE),
    useFilterMemoSelection(FILTER_NAMES.DATEPOST),
    useFilterMemoSelection(FILTER_NAMES.DATEPREV),
  );

  const { selectedValue: metaCategory } = useAppFilterSelectedValue(
    FILTER_NAMES.METACATEGORY,
  );

  useResetFiltersOnMetaCategoryChange(metaCategory, filterSelection);

  const hasRequiredSelections = (filters: FilterSelectedValue[] | null) =>
    REQUIRED_FILTER_IDS.every((id) => {
      const filter = filters?.find((f) => f.id === id);
      return !!filter && filter.values.length > 0;
    });

  // Initial set of applied filters
  useEffect(() => {
    if (!appliedFilterSelection && filterSelection.length > 0) {
      setAppliedFilterSelection(filterSelection);
    }
  }, [filterSelection, appliedFilterSelection]);

  useEffect(() => {
    if (!appliedFilterSelection || !filterSelection) {
      setDifferenceCount(0);
      return;
    }

    let diffCount = 0;

    filterSelection.forEach((currentFilter) => {
      const appliedFilter = appliedFilterSelection.find(
        (f) => f.id === currentFilter.id,
      );

      if (!appliedFilter) {
        if (currentFilter.values.length > 0) diffCount++;
        return;
      }

      if (serializeValues(currentFilter) !== serializeValues(appliedFilter)) {
        diffCount++;
      }
    });

    setDifferenceCount(diffCount);
  }, [filterSelection, appliedFilterSelection]);

  // Updates URL with encoded filters.
  useEncodeFilters(appliedFilterSelection ?? [], true);

  const applyFilters = () => {
    const newFilters = JSON.parse(JSON.stringify(filterSelection));
    setAppliedFilterSelection([...newFilters]);
  };

  const isApplyDisabled = !hasRequiredSelections(filterSelection);

  const contextValue: NetflowFilterContextType = {
    applyFilters,
    filterSelection: appliedFilterSelection,
    differenceCount,
    isApplyDisabled,
  };

  return (
    <NetflowFilterContext.Provider value={contextValue}>
      {children}
    </NetflowFilterContext.Provider>
  );
};

export const useNetflowFilterContext = () => {
  const context = useContext(NetflowFilterContext);

  if (context === undefined) {
    throw new Error(
      'useNetflowFilterContext must be used within a NetflowFilterProvider',
    );
  }

  return context;
};
